import {
  Area, AreaChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { TrendingUp, Undo2 } from "lucide-react";
import type { ReactNode } from "react";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatNumber } from "@/utils/format";
import { EvolucaoVendasStat } from "../chartUtils";
import { TOOLTIP_CURSOR_BAND, lineDots } from "@/utils/rechartsInteraction";

/* ─── Tooltip ─────────────────────────────────────────────────────────────── */
interface TooltipProps {
  active?: boolean;
  payload?: { payload: EvolucaoVendasStat }[];
  label?: string;
  metaUnidades?: number;
}
function CustomTooltip({ active, payload, label, metaUnidades = 0 }: TooltipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-modal p-3 min-w-[240px]">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{label}</p>
      <div className="flex flex-col gap-1 text-sm text-slate-600">
        <div className="flex justify-between gap-4">
          <span>Qtde vendida</span>
          <span className="font-semibold text-slate-800">{formatNumber(d.vendas)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span>Distrato</span>
          <span className="font-semibold text-rose-600">{formatNumber(d.distratos)}</span>
        </div>
        <div className="flex justify-between gap-4 border-t border-slate-100 pt-1 mt-0.5">
          <span>Qtde acumulada</span>
          <span className="font-semibold text-slate-800">
            {formatNumber(d.vendasAcumuladas)}
            {metaUnidades > 0 ? ` / ${formatNumber(metaUnidades)}` : ""}
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span>Distrato acumulado</span>
          <span className="font-semibold text-rose-600">{formatNumber(d.distratosAcumulados)}</span>
        </div>
        <div className="flex justify-between gap-4 border-t border-slate-100 pt-1 mt-0.5">
          <span>% vendido</span>
          <span className="font-bold text-violet-700">{formatNumber(d.percentual, 1)}%</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Componente ─────────────────────────────────────────────────────────── */
interface Props {
  data:         EvolucaoVendasStat[];
  metaUnidades: number;
  loading:      boolean;
}

export function EvolucaoVendas({ data, metaUnidades, loading }: Props) {
  const atual = data[data.length - 1];
  const anterior = data[data.length - 2];
  const variacao = atual ? atual.percentual - (anterior?.percentual ?? 0) : 0;
  const impactoDistratos = atual && metaUnidades > 0
    ? atual.vendasBrutasAcumuladas != null
      ? Math.max(0, ((atual.vendasBrutasAcumuladas - atual.vendasAcumuladas) / metaUnidades) * 100)
      : (atual.distratosAcumulados / metaUnidades) * 100
    : 0;

  return (
    <ChartContainer
      title="Evolução de vendas (%)"
      subtitle="Vendas válidas acumuladas ÷ unidades cadastradas no escopo"
      loading={loading}
      empty={!loading && data.length === 0}
      emptyMessage="Sem vendas ou distratos no período selecionado."
      height="auto"
    >
      <div className="flex flex-col gap-3">
        <div className="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 18, right: 16, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="vendasProgressGradient" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.02} />
                </linearGradient>
              </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis
            dataKey="mes"
            tick={{ fontSize: 11, fill: "#64748b" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
            width={42}
            tickFormatter={(v: number) => `${v}%`}
          />
          <Tooltip content={<CustomTooltip metaUnidades={metaUnidades} />} cursor={TOOLTIP_CURSOR_BAND} />
          <Area
            type="monotone"
            dataKey="percentual"
            name="Avanço"
            stroke="#8b5cf6"
            strokeWidth={2.5}
            fill="url(#vendasProgressGradient)"
            {...lineDots("#8b5cf6", { dotR: 4, activeR: 7 })}
          >
            <LabelList
              dataKey="percentual"
              position="top"
              formatter={(value: unknown) => `${formatNumber(Number(value), 0)}%`}
              fill="#475569"
              fontSize={11}
              fontWeight={600}
            />
          </Area>
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 border-t border-slate-100 pt-3">
          <Indicator icon={<TrendingUp className="w-4 h-4" />} label="Avanço atual" value={`${formatNumber(atual?.percentual ?? 0, 1)}%`} />
          <Indicator icon={<TrendingUp className="w-4 h-4" />} label="Variação vs. mês anterior" value={`${variacao >= 0 ? "+" : ""}${formatNumber(variacao, 1)} p.p.`} className={variacao >= 0 ? "text-emerald-600" : "text-rose-600"} />
          <Indicator icon={<Undo2 className="w-4 h-4" />} label="Impacto dos distratos" value={`-${formatNumber(impactoDistratos, 1)} p.p.`} className="text-rose-600" />
        </div>
      </div>
    </ChartContainer>
  );
}

function Indicator({
  icon,
  label,
  value,
  className = "text-violet-600",
}: {
  icon: ReactNode;
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-1 first:pl-0 sm:border-r sm:last:border-r-0 border-slate-100">
      <span className={className}>{icon}</span>
      <div>
        <p className={`text-sm font-bold ${className}`}>{value}</p>
        <p className="text-[11px] text-slate-500">{label}</p>
      </div>
    </div>
  );
}
